import React, { useEffect, useMemo, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import useStyles, { useChipStyles } from './styles';
import { closeAssignmentModal } from '../../redux/actions/utilsActions';
import { getEntities as getEquipments } from '../../redux/actions/equipmentsActions';
import { clearHierarchy } from '../../redux/actions/hierarchy';
import ChipInput from 'material-ui-chip-input';
import { getIsUserLoaded } from '../../redux/selectors/userSelectors';

import { CorporateRestaurantSelector } from './CorporateRestaurantSelector';
import { FranchiseRestaurantSelector } from './FranchiseRestaurantSelector';
import { useTranslation } from 'react-i18next';
import { getCommonRestraunts } from '../../utils/utils';
import NodeTypeEnum from '../Hierarchy/types/nodeType';

function a11yProps(index: any) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

/**
 * Used for all assignment modals
 * @param assignUnassignToRestaurants
 * @param equipmentSelected
 * @constructor
 */
const AssignmentModal = ({
  assignUnassignToRestaurants,
  equipmentSelected = [],
}) => {
  const dispatch: any = useDispatch();
  const classes = useStyles();
  const chipStyles = useChipStyles();
  const { hierarchy } = useSelector(
    (state: RootStateOrAny) => state.HierarchyReducer,
  );

  const [selectedRestaurants, setSelectedRestaurants] = useState<any>([]);
  const [selectedRestaurantsInactiveTab, setSelectedRestaurantsInactiveTab] =
    useState<any>([]);
  const [assignedRestaurants, setAssignedRestaurants] = useState<{
    displayText: string[];
    ids: number[];
  }>({ displayText: [], ids: [] });
  const [inputValue, setInputValue] = useState('');
  const [assign, setAssign] = useState<number>(0);

  const isUserLoaded = useSelector(getIsUserLoaded);

  let Profile: any = localStorage.getItem('profile') || '';

  if (Profile) {
    Profile = JSON.parse(Profile);
  }

  const isCorporateUser = Profile?.userType === 'corporate_admin';

  const openAssignment = useSelector(
    (state: RootStateOrAny) => state.UtilsReducers.openAssignment,
  );

  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const paginationConfig = useSelector(
    (state: RootStateOrAny) => state.EquipmentsReducer.paginationConfig,
  );

  const restaurantChips = useMemo(
    () => selectedRestaurants.map(node => node.text),
    [selectedRestaurants],
  );
  const hasRestaurantsSelected = useMemo(() => {
    switch (assign) {
      case 0:
        return (
          selectedRestaurants?.length -
            assignedRestaurants?.displayText?.length >
          0
        );
      case 1:
        return selectedRestaurants?.length > 0;
      default:
        return false;
    }
  }, [selectedRestaurants, assignedRestaurants]);
  const canSave = useMemo(
    () => hasRestaurantsSelected,
    [hasRestaurantsSelected],
  );

  const handleChange = (
    event: React.ChangeEvent<unknown>,
    newValue: number,
  ) => {
    setAssign(newValue);
    setSelectedRestaurants(data => {
      setSelectedRestaurantsInactiveTab(data);
      return [...selectedRestaurantsInactiveTab];
    });
  };
  const handleClose = () => {
    dispatch(closeAssignmentModal());
    dispatch(clearHierarchy());
  };

  useEffect(() => {
    if (!openAssignment) {
      setSelectedRestaurants([]);
      setSelectedRestaurantsInactiveTab([]);
      setAssign(0);
    } else {
      const commonRestaurants = getCommonRestraunts(equipmentSelected);
      let value: any[] = [];
      if (!isCorporateUser) {
        value = hierarchy.filter(item =>
          commonRestaurants.includes(item.restaurantId),
        );
        value = value.map(node => ({
          path: node.path,
          text: `${
            node.relatedNumber?.length ? `#     ${node.relatedNumber} | ` : ''
          }${node.name}`,
          type: node.nodeTypeName,
          metas: {
            ...(node.nodeTypeName === NodeTypeEnum.RESTAURANT && {
              restaurantId: Number(node.restaurantId),
            }),
          },
        }));
      }
      setAssignedRestaurants({
        displayText: value.map(item => String(item.text)) || [],
        ids: commonRestaurants,
      });
      setSelectedRestaurants(value || []);
      setSelectedRestaurantsInactiveTab(value);
    }
    //
  }, [openAssignment, hierarchy]);

  const onFranchiseGroupSelect = ({
    restaurants,
    groupId,
    reportUnitId,
    reportUnitName,
    selectedBrand,
    franchiseGroupId,
  }) => {
    let value = restaurants?.filter(item =>
      assignedRestaurants.ids.includes(item.id),
    );
    value = value?.map(restaurant => ({
      text: `# ${restaurant.number} | ${restaurant.name}`,
      path: `/${selectedBrand}/${reportUnitId}/${franchiseGroupId}/${restaurant.id}`,
      type: NodeTypeEnum.RESTAURANT,
      hasChildren: false,
      metas: {
        groupId,
        reportUnitId,
        reportUnitName,
        restaurantId: restaurant.id,
      },
    }));
    setAssignedRestaurants(data => ({
      ...data,
      displayText: value?.map(item => String(item.text)) || [],
    }));
    setSelectedRestaurants(value || []);
    setSelectedRestaurantsInactiveTab(value || []);
  };
  const onClickSave = async () => {
    const restaurants = selectedRestaurants.map(
      restaurant => restaurant.metas?.restaurantId,
    );
    const reportingUnit = 0;
    await assignUnassignToRestaurants(!assign, reportingUnit, restaurants);
    dispatch(
      getEquipments({
        page: paginationConfig.page,
        limit: paginationConfig.rowsPerPage,
        brand: selectedBrand,
      }),
    );
    handleClose();
  };

  const handleRestaurantsSelectionChange = (values: any[]) => {
    // Persist the already assigned restaurants regardless of the toggling of checkboxes for Assign Tab
    if (!assign) {
      const selectedValues = selectedRestaurants.map(item =>
        JSON.stringify(item),
      );
      const stringifiedValues = values.map(item => JSON.stringify(item));
      const set = new Set([
        ...selectedValues.slice(0, assignedRestaurants.displayText.length),
        ...stringifiedValues,
      ]);
      setSelectedRestaurants(Array.from(set).map(item => JSON.parse(item)));
    } else {
      setSelectedRestaurants(values);
    }
  };

  const handleChipDelete = (item, index) => {
    const values = [...selectedRestaurants];
    values.splice(index, 1);
    setSelectedRestaurants(values);
  };

  const { t } = useTranslation();

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={openAssignment}
      onClose={handleClose}
      aria-labelledby="max-width-dialog-title"
      disableBackdropClick={true}
    >
      <DialogContent className={classes.dialogContent}>
        <div className={classes.tabBackground}>
          <DialogTitle id="alert-dialog-title" className={classes.dialogTitle}>
            <div className={classes.divItems}>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {t('home.assignment.selectedRestaurants')}
              </Typography>
              <IconButton
                style={{ position: 'relative', left: '40px', paddingTop: 0 }}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </DialogTitle>
          <AppBar
            elevation={0}
            position="static"
            color="primary"
            className={classes.appBar}
          >
            <Tabs
              value={assign}
              onChange={handleChange}
              indicatorColor="secondary"
              textColor="inherit"
              variant="fullWidth"
              aria-label="full width tabs example"
              className={classes.tabs}
              TabIndicatorProps={{
                style: {
                  display: 'none',
                },
              }}
            >
              <Tab
                className={
                  !assign ? classes.tabSelected : classes.tabNotSelected
                }
                label={t('home.assignment.assign')}
                {...a11yProps(0)}
              />
              <Tab
                className={
                  assign ? classes.tabSelected : classes.tabNotSelected
                }
                label={t('home.assignment.unassign')}
                {...a11yProps(1)}
              />
            </Tabs>
          </AppBar>
          <div className={classes.tabPanel}>
            <TabPanel value={1} index={1}>
              {/* chip input readOnly */}
              <Typography style={{ marginBottom: 15 }} variant="h6">
                {t('home.assignment.selectedRestaurants')}
              </Typography>

              {isUserLoaded && (
                <Box
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'white',
                  }}
                  flexDirection="column"
                  justifyContent="start"
                >
                  <ChipInput
                    label={t('home.assignment.selectedRestaurants')}
                    value={restaurantChips}
                    variant="outlined"
                    fullWidth
                    inputValue={inputValue}
                    onUpdateInput={() => setInputValue('')}
                    classes={chipStyles}
                    chipRenderer={({ chip, value }, key) => (
                      <Chip
                        className={chipStyles.chipInput}
                        key={key}
                        label={chip}
                        onDelete={
                          restaurantChips.length > 0 &&
                          !assign &&
                          assignedRestaurants.displayText.includes(
                            String(value),
                          )
                            ? undefined
                            : () => handleChipDelete(chip, key)
                        }
                      />
                    )}
                  />
                  {isCorporateUser ? (
                    <CorporateRestaurantSelector
                      value={selectedRestaurants}
                      tabIndex={assign}
                      assignedRestaurants={assignedRestaurants}
                      onChange={handleRestaurantsSelectionChange}
                      onFranchiseGroupSelect={onFranchiseGroupSelect}
                    />
                  ) : (
                    <FranchiseRestaurantSelector
                      value={selectedRestaurants}
                      tabIndex={assign}
                      assignedRestaurants={assignedRestaurants}
                      onChange={handleRestaurantsSelectionChange}
                    />
                  )}
                </Box>
              )}
            </TabPanel>
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button variant="outlined" color="primary" onClick={handleClose}>
          {t('app.common.cancel')}
        </Button>
        <Button
          disabled={!canSave}
          variant="contained"
          color="primary"
          onClick={onClickSave}
        >
          {assign ? t('home.assignment.unassign') : t('home.assignment.assign')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentModal;
